/**
 * The page behind a feature tile on the splash.
 *
 * A TILE SAYS WHAT A FEATURE IS IN ONE SENTENCE, which is all a tile should do. Somebody who wants
 * to know whether this product does what their quality system needs is asking a longer question:
 * what does it actually look like, what does it do when somebody gets it wrong, and why does any of
 * it matter in a document people sign. That is what these answer.
 *
 * ONLY A TILE WITH A STORY IS A LINK. A card that opens an empty page is worse than one that does
 * nothing, so the splash reads this registry and links the tiles it finds -- which means stories can
 * be written one at a time without the page ever pointing at something that is not there.
 *
 * EVERY SENTENCE MUST BE TRUE OF THIS SYSTEM TODAY. The same rule the splash itself is written
 * under: this is reached before anybody signs in, so it is the product's claim about itself, and a
 * claim that overstates is one somebody discovers in an evaluation.
 */
import * as React from "react";
import { AuditArt, CoAuthoringArt, ColumnTypesArt, ColumnsArt, FootnoteArt, GlossaryArt,
         CommentArt, CrossReferenceArt, FigureWrapArt, HistoryArt, LifecycleArt,
         RenditionArt, RunningLinesArt, SearchArt, SignedArt, SubmissionGateArt,
         SuggestionArt, VisibilityArt } from "./featureArt";

export type Example = { label: string; body: React.ReactNode };

export type Story = {
    /** The URL. Stable: a link somebody sends is expected to keep working. */
    slug: string;
    /** One line under the title. Longer than the tile's sentence, shorter than a paragraph. */
    hook: string;
    art?: React.ReactNode;
    /** What it does, in prose. Two or three short paragraphs at most. */
    what: string[];
    /** Concrete. A reader believes an example and skims an explanation. */
    examples?: Example[];
    /** The sentence that makes it a QUALITY feature rather than a word-processor one. */
    why: string;
    /** The guide page that covers it, for somebody who has signed in. */
    guide?: { file: string; label: string };
};

export const STORIES: Record<string, Story> = {

    "Columns": {
        slug: "columns",
        hook: "One, two or three, and the words run between them.",
        art: <ColumnsArt/>,
        what: [
            "Set in Page setup, beside orientation and margins, because that is what a column is — "
            + "a property of the page the text is set on, not a format applied to a selection.",
            "The text flows: down the first column, on into the second, and on to the next page. "
            + "It is not a side-by-side layout. That is a table, and a table already prints.",
        ],
        examples: [
            { label: "A two-column work instruction",
              body: "Short numbered steps read better narrow. Two columns put twice as many on a "
                  + "page without shrinking the type, which is what people actually do with a "
                  + "photocopier and a guillotine." },
            { label: "What the screen will not promise",
              body: "Page breaks stop being previewed in a multi-column document. The preview "
                  + "works by pushing a block down the page and the next column is sideways — so "
                  + "rather than draw breaks where the PDF will not break, it draws none and says "
                  + "so where you made the choice." },
        ],
        why: "The PDF is the thing that gets signed, so the measure a line wraps at is decided once "
           + "and both ends read it: the browser flows the columns and the renderer gives the page "
           + "a frame per column, from the same arithmetic.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Footnotes": {
        slug: "footnotes",
        hook: "At the foot of the page the reference landed on — in the PDF as well as on screen.",
        art: <FootnoteArt/>,
        what: [
            "A footnote is the one part of a document whose position is not a property of the "
            + "document: “the foot of this page” means nothing until something has decided where "
            + "the page ends.",
            "So the editor and the renderer read one set of page numbers, and the note is put where "
            + "both will put it. Add one above and everything after renumbers.",
        ],
        examples: [
            { label: "Numbering that suits the document",
              body: "Straight through, from 1 again at each section, or per page. A section is a "
                  + "top-level Heading 1 and nothing else — a heading inside a table cell is not a "
                  + "section, and restarting there would renumber a procedure because somebody put "
                  + "a title in a box." },
            { label: "The Word copy",
              body: "A .docx built this way cannot carry real footnotes, so they are listed at the "
                  + "end under Notes with markers reading [1], [2] — put where they can be read "
                  + "rather than dropped, and the renditions guide says so." },
        ],
        why: "A note that moves between the screen and the signed PDF is a different document on "
           + "paper from the one that was approved.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Column types": {
        slug: "column-types",
        hook: "Say a column holds dates, and the cells that do not are marked as you type.",
        art: <ColumnTypesArt/>,
        what: [
            "Dates, numbers, or one of a list. Declared on the table and indexed by column, so a "
            + "row added next month inherits the constraint rather than quietly escaping it.",
            "The server decides, and the editor says the same thing while somebody is typing so "
            + "they are not told at submission about something they did an hour ago.",
        ],
        examples: [
            { label: "Slashed dates are refused on purpose",
              body: "01/02/2026 is the 1st of February in London and the 2nd of January in Boston. "
                  + "A date in a batch record that means two different days is not a date, so the "
                  + "column asks for 2026-02-01 or 1 Feb 2026." },
            { label: "One of a list",
              body: "Pass / Fail / Not applicable. Anything else is marked — including the empty "
                  + "cell somebody meant to come back to." },
        ],
        why: "EU GMP Annex 11 §6 asks for a check on critical data entered by hand. A column type "
           + "is that check, declared where the data is entered, and the document cannot be sent "
           + "for approval until the cells that break it are fixed.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "The glossary, in the text": {
        slug: "glossary",
        hook: "Approved terms marked where they appear, carrying the definition somebody signed off.",
        art: <GlossaryArt/>,
        what: [
            "Terms your Enterprise Glossary has released are underlined faintly wherever they turn "
            + "up in a procedure. Rest on one and you are shown its approved definition.",
            "Nothing is written into the document. The marks are laid over the text: they do not "
            + "change a word of it, they do not move the content digest, and they never reach the "
            + "signed PDF — so a term released next month lights up in a procedure approved today.",
        ],
        examples: [
            { label: "The longest term wins",
              body: "“Lens Element” is marked as one term rather than as “Lens” followed by a "
                  + "word. Whole words only, so “Lens” is not found inside “lenses” — a mark that "
                  + "is wrong once makes every other mark on the page suspect." },
            { label: "Spell checking stays your browser's",
              body: "A web page cannot add words to it; there is no API for that. So this does the "
                  + "half that is possible and worth more — not “is this spelled right” but “what "
                  + "does this word mean here, and who agreed it”." },
        ],
        why: "A glossary is the agreement about what the words mean. An SOP that uses a term "
           + "differently from the glossary says something nobody approved, and showing the two "
           + "together is how a reviewer notices.",
        guide: { file: "writing-a-document.html", label: "Writing a document" },
    },

    "Co-authoring": {
        slug: "co-authoring",
        hook: "Several people in one procedure at once, each caret where they are.",
        art: <CoAuthoringArt/>,
        what: [
            "Nothing is checked out. Two people typing in the same paragraph merge letter by "
            + "letter rather than one of them being told to wait or being handed a copy.",
            "Open the same document in a second window and both windows keep saving — the one that "
            + "arrives second is not told its work is stale.",
        ],
        examples: [
            { label: "Someone joins while you are writing",
              body: "Their caret appears with their name against it. No reload, no lock, and no "
                  + "“this document is in use by” dialog to be clicked past." },
            { label: "Somebody is offline",
              body: "Their edits arrive when they reconnect and merge with yours." },
        ],
        why: "A procedure is written by the people who do the work and reviewed by the people "
           + "accountable for it. Checking it out makes that a queue.",
        guide: { file: "collaborating.html", label: "Working on a document together" },
    },

    "Suggestions": {
        slug: "suggestions",
        hook: "Propose a change without making it.",
        art: <SuggestionArt/>,
        what: [
            "A reviewer types into the document and nothing in it changes: the words appear as a "
            + "proposal, struck through where they replace something, and the author accepts or "
            + "rejects each one.",
            "Accepting is an edit with a name against it. Rejecting is recorded too, because “this "
            + "was raised and turned down” is a thing a reviewer may need to show later.",
        ],
        why: "A revision cannot be sent for approval while suggestions are still outstanding — an "
           + "unanswered proposal is a question nobody answered, and approving over it would mean "
           + "signing a document somebody had asked about.",
        guide: { file: "reviewing.html", label: "Reviewing" },
    },

    "Outside reviewers": {
        slug: "outside-reviewers",
        hook: "A supplier or a consultant, given exactly one document.",
        art: <VisibilityArt/>,
        what: [
            "Somebody outside the company can be given a single revision to read or comment on, "
            + "without an account that can see anything else.",
            "Every read asks the quality system the same question it asks for an employee: may "
            + "this persona see this revision. There is no second implementation for outsiders, "
            + "which is the only reason it can be trusted.",
        ],
        examples: [
            { label: "Not permitted and not there read the same",
              body: "Somebody who may not see a document gets the same answer as somebody who "
                  + "named a document that was never real. A “forbidden” reply confirms the thing "
                  + "exists, which is the fact the control is there to withhold." },
        ],
        why: "The link to a procedure is the sort of thing that gets pasted into a thread. What "
           + "stops it being an entry point is that the answer depends on who is asking, not on "
           + "who has the address.",
    },

    "Lifecycle gating": {
        slug: "lifecycle-gating",
        hook: "Whether you may type is the record's state, enforced rather than remembered.",
        art: <LifecycleArt/>,
        what: [
            "A revision is open for editing while it is a draft and nobody has sent it for "
            + "approval. The moment it goes out for signature it is read-only, and it stays that "
            + "way once released.",
            "This editor does not decide that. It asks the quality system, on every read and every "
            + "write, and does what it is told — so a document cannot be editable here and frozen "
            + "in the record at the same time.",
        ],
        examples: [
            { label: "A revision out for approval",
              body: "It stays open to read and to comment on — a question about a procedure is "
                  + "most likely to be asked while somebody is being asked to sign it — but not a "
                  + "word of the text can change under the signatures being collected." },
            { label: "The reason is on the page",
              body: "A read-only editor with no explanation reads as a broken one. The line at "
                  + "the top says which state the revision is in and the chip beside the title "
                  + "says the same thing, so nobody has to guess whether it is them or the app." },
        ],
        why: "Everything else here is only as good as this. A text editor that lets you type into "
           + "a released procedure has produced an uncontrolled document that looks exactly like a "
           + "controlled one.",
    },

    "Persona visibility": {
        slug: "persona-visibility",
        hook: "Which documents exist for you is the quality system's answer, not this app's.",
        art: <VisibilityArt/>,
        what: [
            "You sign in as a person and then act as a persona — a position, a group, a part of "
            + "the organisation. What you can see follows that choice, and changing it changes the "
            + "list.",
            "Every read asks the same question of the same authority. There is no second "
            + "implementation of “may this persona see this record”, which is the only reason the "
            + "answer can be relied on.",
        ],
        examples: [
            { label: "The same link, two people",
              body: "A document's address is the sort of thing that gets pasted into a thread. "
                  + "What stops it being a way in is that the answer depends on who is asking." },
            { label: "No persona is no documents",
              body: "A request that names no persona is refused rather than quietly given "
                  + "everything. Failing closed is the only safe direction for a question about "
                  + "access, and it has been the wrong way round in systems before." },
        ],
        why: "A quality system holds things not everyone should read — an investigation naming a "
           + "person, a supplier's confidential method. Visibility is not a convenience feature.",
    },

    "What gets signed": {
        slug: "what-gets-signed",
        hook: "An approval covers a hash of the text, not a promise about it.",
        art: <SignedArt/>,
        what: [
            "When a revision is approved, what the signature records is a digest of the content — "
            + "a number derived from every character of it. Change one letter and it is a "
            + "different number.",
            "So “is this the text that was approved?” is a question with an arithmetic answer "
            + "rather than a procedural one.",
        ],
        examples: [
            { label: "A comment does not change it",
              body: "A comment's anchor is stripped before the digest is taken, so asking a "
                  + "question about a passage changes nothing about what an approval covers. That "
                  + "is deliberate: it is what makes commenting safe during approval." },
            { label: "Unresolved changes block submission",
              body: "A revision carrying suggestions nobody answered, or table cells that "
                  + "contradict their column, cannot be sent for approval. The refusal is the "
                  + "server's, so there is no route round it." },
        ],
        why: "“We believe this is the approved version” is not an answer an inspector has to "
           + "accept. A digest is.",
    },

    "Every edit kept": {
        slug: "every-edit-kept",
        hook: "Any moment can be read back — not only the ones a snapshot happened to catch.",
        art: <HistoryArt/>,
        what: [
            "The editor keeps the operations, not periodic copies. That means the document can be "
            + "reconstructed as it stood at any point, rather than at whichever points a timer "
            + "fired.",
            "Restoring an earlier version is a SAVE, not a rewrite: the trail keeps everything "
            + "that was there, and the restore itself is an event with a name against it.",
        ],
        examples: [
            { label: "“What did this say last Tuesday?”",
              body: "A question that normally needs a backup and somebody who knows where they "
                  + "are kept. Here it is a position on a slider." },
        ],
        why: "When something goes wrong with a product, the question is what the procedure said at "
           + "the time — not what it says now.",
        guide: { file: "version-history.html", label: "Version history" },
    },

    "Named versions": {
        slug: "named-versions",
        hook: "“Issued for review” is something you can find. A timestamp is something you have to remember.",
        art: <HistoryArt/>,
        what: [
            "Any point in the history can be given a name. The named ones stand out in the "
            + "timeline, and they are what people actually go looking for.",
            "Everything else is still kept — naming is not pruning. It marks the moments that "
            + "meant something.",
        ],
        why: "Six months later, “the version we sent to the notified body” is a thing somebody "
           + "needs to produce, and nobody remembers the date.",
        guide: { file: "version-history.html", label: "Version history" },
    },

    "Show changes": {
        slug: "show-changes",
        hook: "What moved between two versions, with the unchanged text dimmed rather than hidden.",
        what: [
            "Pick two points in the history and the difference is drawn into the document: what "
            + "was added, what was taken out, in place.",
            "The text that did not change stays on the page, greyed. A change list that shows only "
            + "the changes takes away the thing that makes them readable.",
        ],
        why: "A reviewer asked to approve a revision is really being asked to approve what is "
           + "DIFFERENT about it, and a sentence out of its paragraph is not reviewable.",
        guide: { file: "version-history.html", label: "Version history" },
    },

    "The rendition": {
        slug: "the-rendition",
        hook: "The PDF the revision is signed as, built on the server and kept.",
        art: <RenditionArt/>,
        what: [
            "Every page carries the document number, the revision, the confidentiality mark and — "
            + "until it is released — its state. The page numbers are real, because the server "
            + "lays the pages out.",
            "It is produced from the text as it stands and kept. Change a word and the next "
            + "download is a fresh one; change nothing and you get the same file back, byte for "
            + "byte.",
        ],
        examples: [
            { label: "Why not just print from the browser",
              body: "A browser cannot put a running header on every page or number a page it has "
                  + "not laid out, and two people printing from two browsers would get two "
                  + "different documents." },
            { label: "It never comes through the application",
              body: "The file is served straight from storage by a link that expires — so a large "
                  + "procedure downloads at full speed and the application is never in the path "
                  + "of the bytes." },
        ],
        why: "The PDF is the artefact that gets filed, emailed to a notified body and handed to an "
           + "inspector. It is the document, more than the screen is.",
        guide: { file: "renditions.html", label: "Renditions" },
    },

    "Watermark": {
        slug: "watermark",
        hook: "A revision that is not released says so across every page.",
        art: <RenditionArt/>,
        what: [
            "DRAFT, IN APPROVAL, SUPERSEDED — written across the sheet, on screen and in the PDF, "
            + "and gone the moment the revision is released.",
            "It is drawn behind the text rather than over it, so it is unmistakable and still "
            + "readable.",
        ],
        why: "The expensive mistake in a quality system is somebody working to a printout of a "
           + "draft. A mark across the page is the one thing that survives being photocopied.",
        guide: { file: "renditions.html", label: "Renditions" },
    },

    "Export to Word": {
        slug: "export-to-word",
        hook: "The copy suppliers and auditors ask for, stamped so it cannot be mistaken for the original.",
        what: [
            "A .docx of the procedure, with every page saying UNCONTROLLED COPY along with the "
            + "number, revision and state.",
            "The guide is explicit about what does not survive the trip — your own header and "
            + "footer, exact page breaks, tab stops at exact positions, the watermark across the "
            + "page, columns, and footnotes at the foot of the page.",
        ],
        why: "People need a copy they can mark up in the tool they have. What matters is that the "
           + "copy says what it is, because the one thing worse than no export is an export "
           + "indistinguishable from the controlled document.",
        guide: { file: "renditions.html", label: "Renditions" },
    },

    "Full-text search": {
        slug: "full-text-search",
        hook: "The words inside your procedures, not just their titles.",
        art: <SearchArt/>,
        what: [
            "Search returns the sentence the match is in, so you can tell which of six results is "
            + "the one you meant without opening all six.",
            "It searches the documents your persona can open, and no others — the same visibility "
            + "question every other read asks.",
        ],
        examples: [
            { label: "Why that matters more than it sounds",
              body: "A search that ignored visibility would be a way to learn what a tenant's "
                  + "procedures say by searching for a word and reading the snippets back. The "
                  + "result list is a read like any other." },
        ],
        why: "A quality system with a thousand procedures in it is only as good as the answer to "
           + "“which one says anything about chamber interlocks?”.",
    },


    "Cross-references": {
        slug: "cross-references",
        hook: "“See section 4” that is still right after section 4 becomes section 3.",
        art: <CrossReferenceArt/>,
        what: [
            "A cross-reference points at a heading, not at a number. The number you read is worked "
            + "out when the document is drawn — on screen and in the PDF alike.",
            "So inserting a section above the one you referred to renumbers the reference with "
            + "everything else, and there is no stale number left behind to find.",
        ],
        examples: [
            { label: "The number is never stored",
              body: "That is the whole design. A stored number is correct until somebody "
                  + "reorganises the document, and then it is wrong in a way nothing detects — "
                  + "the text still reads like a sentence." },
        ],
        why: "A procedure that sends somebody to the wrong section is worse than one that sends "
           + "them nowhere, because they will follow it.",
        guide: { file: "references.html", label: "References" },
    },

    "Text around a figure": {
        slug: "text-around-a-figure",
        hook: "Put the picture against a margin and the words run down its side.",
        art: <FigureWrapArt/>,
        what: [
            "A figure can sit against the left or right margin with the following paragraphs "
            + "beside it, then the text returns to the full measure.",
            "The PDF does the same thing, with the same paragraphs beside the same picture — and "
            + "a heading always clears it, because a section title beside a photograph is a "
            + "procedure that has lost its structure.",
        ],
        why: "A diagram belongs next to the step it illustrates. Pushed to its own line it ends up "
           + "on the next page, away from the words it explains.",
        guide: { file: "figures.html", label: "Figures" },
    },

    "Headers and footers": {
        slug: "headers-and-footers",
        hook: "The document number and revision on every page, filled in by the record.",
        art: <RunningLinesArt/>,
        what: [
            "A running header and footer with your own text, and fields the system fills: the "
            + "document number, the revision, the state, the page number and the count.",
            "They are part of the document rather than a print setting, so what you see is what "
            + "the PDF carries.",
        ],
        examples: [
            { label: "A different first page",
              body: "A cover page usually should not carry the running lines. One checkbox, and "
                  + "page one is left clean." },
        ],
        why: "A page that becomes separated from its document has to be able to say what it "
           + "belongs to. That is the entire reason controlled documents carry running lines.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Comments": {
        slug: "comments",
        hook: "Ask about a passage without changing it — including while it is out for approval.",
        art: <CommentArt/>,
        what: [
            "A comment is anchored to the words it is about, answered in a thread, and resolved "
            + "when it has been dealt with. It stays with the record.",
            "A comment's anchor is stripped before the content digest is taken, which is what "
            + "makes it safe to comment on a revision while signatures are being collected: "
            + "asking a question changes nothing about what the approval covers.",
        ],
        examples: [
            { label: "The conversation belongs to the document",
              body: "A question asked in email or a chat channel is outside the quality system, "
                  + "outside retention, and not something you can hand an inspector. Asked here "
                  + "it is part of the record." },
        ],
        why: "Review is a conversation. If the system has nowhere to hold it, it happens somewhere "
           + "the system cannot see.",
        guide: { file: "commenting.html", label: "Commenting" },
    },

    "Mentions": {
        slug: "mentions",
        hook: "Name somebody in a comment and they are told.",
        art: <CommentArt/>,
        what: [
            "Typing @ offers the people who can already see the document. Choosing one puts a "
            + "resolved reference in the comment and rings their bell.",
            "You can only mention somebody who may open the document, and the check is made again "
            + "when the comment is written rather than trusted from the picker.",
        ],
        examples: [
            { label: "Why the second check exists",
              body: "A filtered picker is a convenience. If the write trusted it, @ would be a "
                  + "way to find out who works here — and a notification carrying a document's "
                  + "title would land with somebody who may not open it." },
        ],
        why: "Getting the right person to look at the right paragraph is most of what review is, "
           + "and “I emailed you about it” is not in the record.",
        guide: { file: "commenting.html", label: "Commenting" },
    },

    "Templates": {
        slug: "templates",
        hook: "Start from your own SOP shell instead of a blank page.",
        what: [
            "A template is a controlled document somebody ticked a box on. It has an owner, a "
            + "revision, an approval and an audit trail, because it IS a document — not a new kind "
            + "of thing with a lifecycle nobody has designed.",
            "Starting from one takes a copy of the text. Editing the template afterwards changes "
            + "nothing already started from it.",
        ],
        examples: [
            { label: "That is the opposite of most software",
              body: "Elsewhere “template” usually means a living master. Here a released "
                  + "procedure whose words could change because somebody edited a shell somewhere "
                  + "else would not be a controlled procedure, so the dialog says which it is "
                  + "rather than assuming you know." },
        ],
        why: "“The approved SOP template” is a thing every quality system has and most of them "
           + "keep in a folder nobody controls.",
    },

    "Named styles": {
        slug: "named-styles",
        hook: "Format one heading the way you want it, then make every heading of that level follow.",
        what: [
            "Styles live on the document. Change Heading 2 once and every Heading 2 follows, on "
            + "screen and in the PDF.",
            "There is an update-to-match: format a heading directly, then tell the style to take "
            + "the new look, which is how people actually work rather than how a style dialog "
            + "assumes they do.",
        ],
        why: "A procedure where the headings are each formatted by hand is one where a reader "
           + "cannot tell a section from a subsection, and the structure is the thing they are "
           + "navigating by.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Import from Word": {
        slug: "import-from-word",
        hook: "Bring an existing procedure in, and have the import recorded as its own event.",
        what: [
            "Headings, lists, tables and images come across into a real document rather than into "
            + "a box of text.",
            "The import is an event in the trail, so the document does not simply appear with no "
            + "account of where it came from.",
        ],
        why: "Nobody adopts a document system by retyping four hundred procedures. The question is "
           + "whether what arrives is a controlled document or a paste.",
    },

    "Page setup": {
        slug: "page-setup",
        hook: "Orientation, margins and columns — read by the screen, the print stylesheet and the server.",
        what: [
            "Portrait or landscape, margins on all four sides, where the running lines sit, and "
            + "how many columns the text flows down.",
            "Four things used to hold their own idea of “8.5 by 11 with an inch round it”, and the "
            + "ruler's copy was already a tenth of an inch out from the paper's. There is one "
            + "derivation now, and the ruler, the page preview, the stylesheet and the renderer "
            + "all read it.",
        ],
        why: "A preview that breaks pages in one place and a PDF that breaks them in another is "
           + "worse than having no preview at all.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Page preview": {
        slug: "page-preview",
        hook: "Where the pages break, drawn on the paper while you write.",
        what: [
            "A document used to be one very long page. Writing a procedure means knowing what "
            + "lands on page three, whether a table fits, and whether a heading is about to be "
            + "stranded at the foot of a sheet with its section overleaf.",
            "It is a preview, not a layout engine: nothing is stored and nothing enters the "
            + "document. It measures the blocks the browser has already laid out and shows the "
            + "breaks from the same numbers the renderer uses.",
        ],
        why: "The alternative is finding out at the printer, which in a quality system means "
           + "finding out after the signatures.",
    },


    "Rich text": {
        slug: "rich-text",
        hook: "The ordinary business of writing a document, done properly.",
        what: [
            "Headings, lists, check-lists, alignment, spacing, bold and italic, links, symbols. "
            + "The things nobody notices until one of them is missing.",
            "The menu is the honest inventory: anything greyed out says why when you hover, rather "
            + "than being quietly absent.",
        ],
        why: "A procedure is written by somebody with a job to do. Fighting the editor is time "
           + "taken from getting the words right.",
        guide: { file: "writing-a-document.html", label: "Writing a document" },
    },

    "The ruler": {
        slug: "the-ruler",
        hook: "Indents and hanging indents, dragged where you want them.",
        what: [
            "First-line and hanging indents on the ruler, in inches that match the page — because "
            + "the ruler, the paper and the renderer take their geometry from one derivation.",
            "A hanging indent is how a numbered clause is written: the number sits out and the "
            + "text lines up under itself.",
        ],
        why: "A clause whose continuation lines run back under its number is harder to read and "
           + "harder to cite, and citing clauses is what auditors do.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Tab stops": {
        slug: "tab-stops",
        hook: "Columns that stay lined up when the words in front of them change.",
        what: [
            "Left, centre, right and decimal stops, set on the ruler and honoured in the PDF.",
            "Unlike spaces, a tab stop is a position rather than a guess — so editing the text "
            + "before it does not shunt everything after it out of line.",
        ],
        why: "A signature block or a short parameter list held together by spaces falls apart the "
           + "first time somebody corrects a word, and usually after it was approved.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Tables": {
        slug: "tables",
        hook: "Borders per edge, column widths, a pinned header row, and a floor under the signature block.",
        art: <ColumnTypesArt/>,
        what: [
            "Per-edge borders rather than one setting for the whole table, column widths that "
            + "survive a row being added, a header row that repeats, and a minimum row height.",
            "All of it drawn the same way in the PDF, which is where most editors and their "
            + "printers part company.",
        ],
        examples: [
            { label: "A minimum row height is not cosmetic",
              body: "The signature block in a batch record needs room to sign in. A row that "
                  + "shrinks to its text gives somebody three millimetres and a biro." },
        ],
        why: "Most of the data in a quality system is in a table — results, steps, sign-offs — and "
           + "a table that prints differently from how it looked is a record somebody will "
           + "question.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Figures": {
        slug: "figures",
        hook: "Stored on the record, so a document full of images still opens at the speed of its text.",
        art: <FigureWrapArt/>,
        what: [
            "Images are attachments on the revision rather than data buried in the document, with "
            + "captions that number themselves.",
            "That is why a procedure with thirty photographs opens as quickly as one with none: "
            + "the text arrives first and the pictures follow.",
        ],
        examples: [
            { label: "A figure cannot be borrowed",
              body: "The renderer will only draw a picture that is attached to the revision being "
                  + "rendered. A document cannot name an attachment belonging to another record "
                  + "and have it appear." },
        ],
        why: "Half of what a work instruction says is a photograph of the thing being done.",
        guide: { file: "figures.html", label: "Figures" },
    },

    "Numbered headings": {
        slug: "numbered-headings",
        hook: "1, 1.1, 1.1.1 — computed from the document's shape, never typed.",
        art: <CrossReferenceArt/>,
        what: [
            "Turn them on and the numbers come from the structure. Move a section and everything "
            + "renumbers, including every cross-reference pointing at it.",
            "Off unless somebody turns them on: numbering every existing procedure the day this "
            + "shipped would have changed what all of them print, with nothing in any record "
            + "saying why.",
        ],
        why: "Hand-typed section numbers are correct exactly until the first reorganisation, and "
           + "wrong silently thereafter.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Bookmarks": {
        slug: "bookmarks",
        hook: "Point a reference at any passage you choose, not only at a heading.",
        what: [
            "Mark a sentence, a table, a step — and refer to it from elsewhere. The reference "
            + "follows it if it moves.",
            "It is the same machinery as a cross-reference to a heading, with the target being "
            + "something you chose rather than something the document's structure gave you.",
        ],
        why: "“As described in the third paragraph of 4.2” is the sort of thing procedures say, "
           + "and it is the sort of thing that goes wrong when 4.2 gains a paragraph.",
        guide: { file: "references.html", label: "References" },
    },

    "Table of contents": {
        slug: "table-of-contents",
        hook: "The document's actual shape, not a copy of it.",
        what: [
            "Insert one and it reads the headings as they are. There is nothing to refresh, "
            + "because it is not a snapshot that could be stale.",
            "In the PDF it carries page numbers, worked out when the pages are laid out.",
        ],
        why: "A contents page that disagrees with the document is a defect an auditor will find in "
           + "about a minute, and every system where it has to be refreshed by hand produces one "
           + "eventually.",
        guide: { file: "references.html", label: "References" },
    },

    "Outline pane": {
        slug: "outline-pane",
        hook: "Navigate a long procedure by its structure, with the section you are in marked.",
        what: [
            "The headings down the side, the one you are in highlighted, and a click to move. It "
            + "follows as you scroll.",
            "A heading inside a table cell is not a section — the outline uses the same rule the "
            + "numbering does, so the two agree about what the document's shape is.",
        ],
        why: "Procedures get long. Scrolling is not navigation.",
    },

    "Page breaks": {
        slug: "page-breaks",
        hook: "Decide where a page ends yourself, and the PDF breaks there too.",
        what: [
            "Insert a break and both the preview and the renderer honour it.",
            "It is a node in the document rather than a formatting hint, so it travels with the "
            + "text and is part of what gets approved.",
        ],
        why: "“Each test on its own page” is a real requirement in a batch record, and it has to "
           + "survive the trip to paper.",
        guide: { file: "formatting-and-layout.html", label: "Formatting and layout" },
    },

    "Find and replace": {
        slug: "find-and-replace",
        hook: "Across the whole document, including the parts you are not looking at.",
        what: [
            "Find, replace, replace all — through the entire procedure rather than the portion "
            + "currently on screen.",
        ],
        why: "A part number changes and it appears in eleven places. Ten of them is a defect.",
    },

    "Word count": {
        slug: "word-count",
        hook: "For the document, or for what you have selected.",
        what: [
            "Words and characters, for everything or for a selection.",
        ],
        why: "Small, and the sort of thing whose absence is noticed immediately by anybody writing "
           + "to a limit.",
    },

    "Page-one previews": {
        slug: "page-one-previews",
        hook: "Recognise a document rather than read its number.",
        what: [
            "Each document in the list shows its own first page — words and figures in their "
            + "places, small but true to the page.",
            "The picture is built from the same rendition the PDF comes from, so it is the "
            + "document rather than an approximation of it.",
        ],
        why: "People remember what a procedure looks like long before they remember that it is "
           + "SOP-00311.",
    },

    "Filters and sorting": {
        slug: "filters-and-sorting",
        hook: "By state, by author, by when it last moved — with the count beside each filter before you apply it.",
        what: [
            "The counts are the point. A filter that returns nothing is a wasted click, and a "
            + "list of filters with no counts is a row of wasted clicks waiting to happen.",
            "Every count respects the same visibility rule as the list itself, so a filter cannot "
            + "report documents you could not open.",
        ],
        why: "A quality system accumulates. Finding the four drafts somebody left behind is a "
           + "weekly job, not an occasional one.",
    },

    "Account policy": {
        slug: "account-policy",
        hook: "Decide for yourself whether an unanswered question stops a release.",
        what: [
            "Some organisations want an open comment to block submission; others treat comments "
            + "as conversation and suggestions as the thing that must be resolved. This is a "
            + "setting rather than our opinion.",
            "What is not a setting: a table cell that contradicts its declared column always "
            + "blocks submission. That is not a judgement call — the cell is simply wrong.",
        ],
        why: "Quality systems differ, and a product that imposes one organisation's conventions on "
           + "another is one that gets configured around.",
    },

    "Browse by time": {
        slug: "browse-by-time",
        hook: "Drag back through the document's own moments.",
        art: <HistoryArt/>,
        what: [
            "A slider across the history. Named versions and imports are marked along it, so the "
            + "moments that meant something are the ones you can aim at.",
            "Reading an old moment changes nothing — you are looking, not restoring.",
        ],
        why: "“When did this sentence appear, and in whose edit?” is the question behind most "
           + "document investigations.",
        guide: { file: "version-history.html", label: "Version history" },
    },

    "Grouped history": {
        slug: "grouped-history",
        hook: "A day of typing reads as a dozen entries rather than two hundred.",
        art: <HistoryArt/>,
        what: [
            "Consecutive edits by the same person in the same sitting are shown as one entry. "
            + "Nothing is discarded — the grouping is in the reading, not in the storage.",
            "A named version is never folded into a group, because it is the thing somebody "
            + "deliberately marked.",
        ],
        why: "A trail nobody can read is a trail nobody reads, and two hundred entries for one "
           + "afternoon is the usual way an audit trail becomes decorative.",
        guide: { file: "version-history.html", label: "Version history" },
    },

    "Guides": {
        slug: "guides",
        hook: "Written for the people doing the work, and kept honest about what is not built.",
        what: [
            "Guides covering writing, formatting, figures, references, collaborating, commenting, "
            + "reviewing, version history and renditions.",
            "Each has a “things that surprise people” table, which is where the honest answers "
            + "live — including what does not survive the Word export and why.",
        ],
        why: "Documentation that only describes the happy path is documentation people stop "
           + "trusting the first time something surprises them.",
    },

};

/** Is there a page behind this tile? The splash asks before it makes one a link. */
export const storyFor = (name: string): Story | undefined => STORIES[name];

export const storyBySlug = (slug: string): Story | undefined =>
    Object.values(STORIES).find(one => one.slug === slug);

/** The tile's own name, from a slug -- the story page shows it as its title. */
export const nameBySlug = (slug: string): string | undefined =>
    Object.keys(STORIES).find(name => STORIES[name].slug === slug);
